import { useState, useRef, useEffect } from 'react';
import { X, Camera, RefreshCw, Plus, Trash2, ClipboardList } from 'lucide-react';
// import { CreateTaskRequest } from '../../types/jobcard';
// import { createTask } from '../../services/jobcardApi';
import { toast } from 'react-hot-toast';
import AddTaskEquipmentModal from './AddTaskEquipmentModal';
import { Equipment } from '../../types/equipment';
import { Store } from '../../types/store';
import { getStores } from '../../services/storeApi';
import { getEquipment } from '../../services/equipmentApi';
// import { Dialog } from '@headlessui/react';
import { compressImage, formatFileSize } from '../../utils/imageUtils';
import { showErrorToast } from '../../utils/toast';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { getSops, startSopCompletion, Sop } from '../../services/sopApi';
import { useNavigate } from 'react-router-dom';
// import SelectEquipmentModal from './SelectEquipmentModal';

// interface PaginatedResponse<T> {
//     current_page: number;
//     data: T[];
//     first_page_url: string;
//     from: numbe
//     last_page: number;
//     last_page_url: string;
//     links: Array<{ url: string | null; label: string; active: boolean }>;
//     next_page_url: string | null;
//     path: string;
//     per_page: number;
//     prev_page_url: string | null;
//     to: number;
//     total: number;
// }

interface Props {
    jobCardId: number;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    jobcardStoreNumber?: string;
    jobcardStoreName?: string;
    jobNumber?: string;
}

export default function CreateTaskModal({ jobCardId, isOpen, onClose, onSuccess, jobcardStoreNumber, jobcardStoreName, jobNumber }: Props) {
    const { isAdmin, user } = useAuth();
    const navigate = useNavigate();
    const hasEquipmentAccess = Boolean(isAdmin || user?.position?.department?.name === 'IT Department');
    const isITDepartment = Boolean(user?.position?.department?.name === 'IT Department');
    const [description, setDescription] = useState('');
    const [pictures, setPictures] = useState<File[]>([]);
    const [showCamera, setShowCamera] = useState(false);
    const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
    const [isLoading, setIsLoading] = useState(false);
    const [showEquipmentModal, setShowEquipmentModal] = useState(false);
    const [activeTab, setActiveTab] = useState<'regular' | 'sop'>('regular');
    const [sops, setSops] = useState<Sop[]>([]);
    const [selectedSop, setSelectedSop] = useState<Sop | null>(null);
    // Extract KSA from store information
    const extractKSA = (storeInfo?: string) => {
        if (!storeInfo) return '';
        // Look for KSA pattern like "KSA1440" or "#KSA1554"
        const match = storeInfo.match(/#?KSA\d+/i);
        return match ? match[0].replace('#', '') : '';
    };

    const [ksaNumber, setKsaNumber] = useState(() => {
        // Try to extract from store name first, then store number, then job number
        const fromStoreName = extractKSA(jobcardStoreName);
        const fromStoreNumber = extractKSA(jobcardStoreNumber);
        return fromStoreName || fromStoreNumber || jobNumber || '';
    });
    const [equipment, setEquipment] = useState<{
        data: {
            current_page: number;
            data: Equipment[];
            first_page_url: string;
            from: number;
            last_page: number;
            last_page_url: string;
            links: Array<{ url: string | null; label: string; active: boolean }>;
            next_page_url: string | null;
            path: string;
            per_page: number;
            prev_page_url: string | null;
            to: number;
            total: number;
        };
        success: boolean;
    }>({
        data: {
            current_page: 1,
            data: [],
            first_page_url: '',
            from: 1,
            last_page: 1,
            last_page_url: '',
            links: [],
            next_page_url: null,
            path: '',
            per_page: 10,
            prev_page_url: null,
            to: 0,
            total: 0
        },
        success: true
    });
    const [stores, setStores] = useState<Store[]>([]);
    const [selectedEquipment, setSelectedEquipment] = useState<(Equipment & {
        allocationType: 'store' | 'equipment';
        storeId?: number;
        allocatedId?: number;
        allocationDate: string;
        allocationNotes?: string;
        store_number?: string;
        quantity?: number;
    })[]>([]);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const promises = [];
                
                if (hasEquipmentAccess) {
                    // Only fetch equipment and stores if user has equipment access
                    promises.push(
                        getStores(),
                        getEquipment({
                            per_page: -1,
                            allocated: false // Only show unallocated equipment
                        })
                    );
                }
                
                if (isITDepartment) {
                    // Fetch SOPs for IT department
                    promises.push(getSops());
                }
                
                const results = await Promise.all(promises);
                
                if (hasEquipmentAccess) {
                    setStores(results[0].data.data);
                    setEquipment(results[1].data);
                    
                    if (isITDepartment && results[2]) {
                        setSops(results[2].data);
                    }
                } else if (isITDepartment) {
                    setSops(results[0].data);
                }
            } catch (error) {
                console.error('Failed to fetch data:', error);
                toast.error('Failed to fetch data');
            }
        };

        if (isOpen) {
            fetchData();
        }
    }, [isOpen, hasEquipmentAccess, isITDepartment]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        
        if (activeTab === 'sop') {
            if (!ksaNumber.trim()) {
                toast.error('KSA number is required');
                return;
            }
            if (!selectedSop) {
                toast.error('Please select a SOP');
                return;
            }
            await handleSopSubmit();
            return;
        }
        
        if (!description.trim()) {
            toast.error('Description is required');
            return;
        }

        try {
            setIsLoading(true);
            
            // Create a FormData object for proper file handling
            const formData = new FormData();
            formData.append('description', description.trim());
            
            // Add equipment data - always send an array, even if empty
            if (hasEquipmentAccess && selectedEquipment.length > 0) {
                selectedEquipment.forEach((eq, index) => {
                    formData.append(`equipment[${index}][equipment_id]`, eq.id.toString());
                    formData.append(`equipment[${index}][allocation_type]`, eq.allocationType);
                    
                    if (eq.allocationType === 'equipment' && eq.allocatedId) {
                        formData.append(`equipment[${index}][allocated_id]`, eq.allocatedId.toString());
                    }
                    
                    if (eq.allocationType === 'store') {
                        if (eq.storeId) formData.append(`equipment[${index}][store_id]`, eq.storeId.toString());
                        if (eq.store_number) formData.append(`equipment[${index}][store_number]`, eq.store_number);
                    }
                    
                    formData.append(`equipment[${index}][allocation_date]`, eq.allocationDate);
                    if (eq.allocationNotes) formData.append(`equipment[${index}][allocation_notes]`, eq.allocationNotes);
                    
                    // Ensure quantity is properly set for both grouped and non-grouped items
                    const quantity = eq.quantity && eq.quantity > 0 ? eq.quantity : 1;
                    formData.append(`equipment[${index}][quantity]`, quantity.toString());
                    
                    if (eq.groupedIds && eq.groupedIds.length > 0) {
                        eq.groupedIds.forEach((groupId, groupIndex) => {
                            formData.append(`equipment[${index}][groupedIds][${groupIndex}]`, groupId.toString());
                        });
                    }
                });
            } else {
                // If no equipment, send an empty value that Laravel will treat as an empty array
                formData.append('equipment', '');
                console.log('No equipment to submit - sending empty equipment field');
            }
            
            // Add pictures - ensure field name matches what backend expects
            if (pictures.length > 0) {
                console.log(`Adding ${pictures.length} pictures to form data`);
                pictures.forEach((file, index) => {
                    // Check that the file is valid
                    if (!(file instanceof File)) {
                        console.error('Invalid file object:', file);
                        return;
                    }
                    // Use the same format as in EditTaskModal
                    formData.append(`pictures[]`, file);
                    console.log(`Added picture ${index}: ${file.name}, size: ${file.size}`);
                });
            }
            
            // Log form data for debugging
            console.log('Form data entries:');
            for (const pair of formData.entries()) {
                console.log(`${pair[0]}: ${typeof pair[1] === 'object' ? 'File: ' + (pair[1] as File).name : pair[1]}`);
            }
            
            // Use direct API call with FormData
            const response = await api.post(`/jobcards/${jobCardId}/tasks`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            console.log('Task creation response:', response);
            
            toast.success('Task created successfully');
            onSuccess();
            // Reset form
            setDescription('');
            setSelectedEquipment([]);
            setPictures([]);
            // Reset KSA with same extraction logic
            const fromStoreName = extractKSA(jobcardStoreName);
            const fromStoreNumber = extractKSA(jobcardStoreNumber);
            setKsaNumber(fromStoreName || fromStoreNumber || jobNumber || '');
            onClose();
        } catch (err: unknown) {
            console.error('Error creating task:', err);
            
            // Type guard for axios errors
            const axiosError = err as { response?: { data?: { errors?: Record<string, string[]>; message?: string } }; message?: string };
            
            console.log('Error response data:', axiosError.response?.data);
            console.log('Validation errors:', axiosError.response?.data?.errors);
            
            // More detailed error message
            let errorMessage = 'Failed to create task';
            if (axiosError.response?.data?.errors) {
                const errorDetails = Object.entries(axiosError.response.data.errors)
                    .map(([field, errors]) => `${field}: ${errors}`)
                    .join(', ');
                errorMessage = `Validation failed: ${errorDetails}`;
            } else if (axiosError.response?.data?.message) {
                errorMessage = axiosError.response.data.message;
            } else if (axiosError.message) {
                errorMessage = axiosError.message;
            }
            
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const startCamera = async () => {
        try {
            // Stop any existing stream first
            if (videoRef.current?.srcObject) {
                const stream = videoRef.current.srcObject as MediaStream;
                stream.getTracks().forEach(track => track.stop());
            }

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: facingMode,
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    aspectRatio: { ideal: 16 / 9 }
                }
            });

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.style.transform = 'none';
                videoRef.current.style.width = '100%';
                videoRef.current.style.height = 'auto';
                videoRef.current.style.maxHeight = '100vh';
                videoRef.current.style.objectFit = 'cover';
                videoRef.current.style.maxWidth = '100vw';

                await videoRef.current.play();
            }
            setShowCamera(true);
        } catch (err) {
            console.error('Error accessing camera:', err);
            showErrorToast('Failed to access camera. Please check your permissions.');
        }
    };

    useEffect(() => {
        if (showCamera) {
            startCamera();
        }
    }, [showCamera, facingMode]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        try {
            const compressedFiles: File[] = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                if (!file.type.startsWith('image/')) {
                    showErrorToast(`${file.name} is not an image file`);
                    continue;
                }

                const originalSize = formatFileSize(file.size);
                const compressedFile = await compressImage(file);
                const compressedSize = formatFileSize(compressedFile.size);
                
                console.log(`Compressed ${file.name}: ${originalSize} → ${compressedSize}`);
                compressedFiles.push(compressedFile);
            }

            setPictures(prev => [...prev, ...compressedFiles]);
        } catch (error) {
            console.error('Error processing images:', error);
            showErrorToast('Failed to process images');
        }
    };

    const handleSwapCamera = async () => {
        setFacingMode(current => current === 'environment' ? 'user' : 'environment');
        await startCamera();
    };

    const handleCameraCapture = async () => {
        if (!videoRef.current) return;

        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Draw the video frame to the canvas
        ctx.drawImage(videoRef.current, 0, 0);

        // Convert the canvas to a blob
        canvas.toBlob(async (blob) => {
            if (!blob) return;

            // Create a File object from the blob
            const file = new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' });
            setPictures(prev => [...prev, file]);

            // Stop the camera stream
            if (videoRef.current?.srcObject) {
                const stream = videoRef.current.srcObject as MediaStream;
                stream.getTracks().forEach(track => track.stop());
            }
            setShowCamera(false);
        }, 'image/jpeg');
    };

    const removePicture = (index: number) => {
        setPictures(prev => prev.filter((_, i) => i !== index));
    };

    const handleSopSubmit = async () => {
        if (!selectedSop || !user) return;

        try {
            setIsLoading(true);
            
            // Start SOP completion
            const completionResponse = await startSopCompletion(selectedSop.id, {
                completed_by: user.id, // Send user ID as number
                technician_name: user.name || user.email,
                ksa_number: ksaNumber,
                job_id: jobCardId, // Pass the job card ID to link SOP completion to the task
                ...(jobcardStoreNumber && stores.length > 0 && {
                    store_id: stores.find(s => s.storenumber === jobcardStoreNumber)?.id
                })
            });

            // Create a task with SOP information
            const formData = new FormData();
            formData.append('description', `SOP: ${selectedSop.title}`);
            formData.append('sop_completion_id', completionResponse.data.id.toString());
            formData.append('task_type', 'sop');
            
            // Add pictures if any
            pictures.forEach((file) => {
                formData.append(`pictures[]`, file);
            });
            
            await api.post(`/jobcards/${jobCardId}/tasks`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            toast.success('SOP task created successfully');
            onSuccess();
            onClose();
            
            // Navigate to SOP completion page with job card information
            navigate(`/sop-completion/${completionResponse.data.id}`, {
                state: {
                    jobCardId,
                    jobNumber,
                    jobcardStoreNumber,
                    jobcardStoreName
                }
            });
            
            // Reset form
            setDescription('');
            setSelectedSop(null);
            // Reset KSA with same extraction logic
            const fromStoreName = extractKSA(jobcardStoreName);
            const fromStoreNumber = extractKSA(jobcardStoreNumber);
            setKsaNumber(fromStoreName || fromStoreNumber || jobNumber || '');
            setPictures([]);
            setActiveTab('regular');
            
        } catch (error: unknown) {
            console.error('Error creating SOP task:', error);
            
            // Type guard for axios errors
            const axiosError = error as { response?: { data?: { message?: string } } };
            toast.error(axiosError.response?.data?.message || 'Failed to create SOP task');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSopSelect = async (sop: Sop) => {
        setSelectedSop(sop);
    };

    const handleAddEquipment = (selectedItems: { 
        id: number; 
        quantity: number; 
        order_number?: string;
        name?: string;
        serial_number?: string;
        equipment_type?: {
            name: string;
            id?: number;
        };
        equipment_subtype_id?: number;
        groupedIds?: number[];
    }[]) => {
        console.log('Current equipment state:', equipment);
        console.log('Raw selected items:', JSON.stringify(selectedItems, null, 2));

        // Process each item individually to ensure unique IDs
        const newEquipment = selectedItems.map(item => {
            // For non-grouped items or items without groupedIds
            if (!item.groupedIds || item.groupedIds.length === 0) {
                console.log(`Processing direct item: ${item.name} (ID: ${item.id})`);
                const matchingStore = stores.find(s => s.storenumber === jobcardStoreNumber);
                
                return {
                    id: item.id,
                    name: item.name || '',
                    serial_number: item.serial_number,
                    equipment_type: item.equipment_type,
                    equipment_subtype_id: item.equipment_subtype_id,
                    order_number: item.order_number,
                    allocationType: 'store' as const,
                    storeId: matchingStore?.id,
                    store_number: jobcardStoreNumber,
                    allocationDate: new Date().toISOString().split('T')[0],
                    quantity: item.quantity || 1 // Use item.quantity instead of hardcoding 1
                };
            }
            
            // This is a grouped item
            console.log(`Processing grouped item: ${item.name} (ID: ${item.id}) with ${item.groupedIds.length} items`);
            const matchingStore = stores.find(s => s.storenumber === jobcardStoreNumber);
            
            return {
                id: item.id,
                name: item.name || '',
                serial_number: item.serial_number,
                equipment_type: item.equipment_type,
                equipment_subtype_id: item.equipment_subtype_id,
                order_number: item.order_number,
                allocationType: 'store' as const,
                storeId: matchingStore?.id,
                store_number: jobcardStoreNumber,
                allocationDate: new Date().toISOString().split('T')[0],
                quantity: item.quantity || 1, // Ensure we have a fallback to 1
                groupedIds: item.groupedIds
            };
        });

        console.log('New equipment to add:', JSON.stringify(newEquipment, null, 2));
        
        // Filter out any duplicates based on ID
        const uniqueEquipmentMap = new Map();
        [...selectedEquipment, ...newEquipment].forEach(item => {
            if (!uniqueEquipmentMap.has(item.id)) {
                uniqueEquipmentMap.set(item.id, item);
            }
        });
        
        setSelectedEquipment(Array.from(uniqueEquipmentMap.values()));
        setShowEquipmentModal(false);
    };

    if (!isOpen) return null;

    if (showCamera) {
        return (
            <div className="fixed inset-0 bg-black dark:bg-gray-900 z-50">
                <div className="relative h-full">
                    <video
                        ref={videoRef}
                        className="w-full h-full object-cover"
                        autoPlay
                        playsInline
                        muted
                    />
                    <div className="absolute bottom-8 inset-x-0 flex justify-center space-x-4 px-4">
                        <button
                            type="button"
                            onClick={handleSwapCamera}
                            className="p-4 bg-white/20 dark:bg-gray-800/40 backdrop-blur-sm rounded-full shadow-lg hover:bg-white/30 dark:hover:bg-gray-800/60 transition-colors duration-200"
                        >
                            <RefreshCw size={24} className="text-white dark:text-gray-200" />
                        </button>
                        <button
                            type="button"
                            onClick={handleCameraCapture}
                            className="p-6 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                        >
                            <Camera size={32} className="text-gray-900 dark:text-gray-200" />
                        </button>
                    </div>
                    <button
                        onClick={() => {
                            const stream = videoRef.current?.srcObject as MediaStream;
                            if (stream) {
                                stream.getTracks().forEach(track => track.stop());
                            }
                            setShowCamera(false);
                        }}
                        className="absolute top-4 left-4 p-4 text-white dark:text-gray-200 hover:text-gray-200 dark:hover:text-gray-300 transition-colors duration-200"
                    >
                        <X size={32} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center overflow-y-auto p-4">
            <div className="relative w-full max-w-3xl mx-auto my-8">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl">
                    {/* Header - Fixed */}
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Create New Task
                            </h2>
                            <button
                                onClick={onClose}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        
                        {/* Tabs - Only show for IT Department */}
                        {isITDepartment && (
                            <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('regular')}
                                    className={`flex-1 flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        activeTab === 'regular'
                                            ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                                >
                                    Regular Task
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('sop')}
                                    className={`flex-1 flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        activeTab === 'sop'
                                            ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                                >
                                    <ClipboardList size={16} className="mr-2" />
                                    SOP Task
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Content - Scrollable */}
                    <div className="px-6 py-4 max-h-[calc(100vh-16rem)] overflow-y-auto">
                        <form id="create-task-form" onSubmit={handleSubmit} className="space-y-6">
                            {activeTab === 'regular' ? (
                                <>
                                    {/* Description Field */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Description *
                                        </label>
                                        <textarea
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                                            rows={4}
                                            placeholder="Enter task description..."
                                            required
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* KSA Number */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            KSA Number *
                                        </label>
                                        <input
                                            type="text"
                                            value={ksaNumber}
                                            onChange={(e) => setKsaNumber(e.target.value)}
                                            className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            placeholder="Enter KSA number..."
                                            required
                                        />
                                    </div>

                                    {/* SOP Selection */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Select SOP *
                                        </label>
                                        <select
                                            value={selectedSop?.id || ''}
                                            onChange={(e) => {
                                                const sopId = parseInt(e.target.value);
                                                const sop = sops.find(s => s.id === sopId);
                                                if (sop) handleSopSelect(sop);
                                            }}
                                            className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            required
                                        >
                                            <option value="">Choose a SOP...</option>
                                            {sops.map(sop => (
                                                <option key={sop.id} value={sop.id}>
                                                    {sop.category} - {sop.title}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* SOP Details */}
                                    {selectedSop && (
                                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                            <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                                                {selectedSop.title}
                                            </h3>
                                            {selectedSop.description && (
                                                <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                                                    {selectedSop.description}
                                                </p>
                                            )}
                                            <div className="text-sm text-blue-700 dark:text-blue-300">
                                                <p><strong>Category:</strong> {selectedSop.category}</p>
                                                {selectedSop.estimated_duration && (
                                                    <p><strong>Estimated Duration:</strong> {selectedSop.estimated_duration} minutes</p>
                                                )}
                                                <p><strong>Steps:</strong> {selectedSop.steps?.length || 0} steps</p>
                                                <p><strong>Required Steps:</strong> {selectedSop.steps?.filter(s => s.is_required).length || 0}</p>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Equipment Section */}
                            {hasEquipmentAccess && (
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                    <div className="flex justify-between items-center mb-4">
                                        <div className="flex items-baseline gap-2">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Equipment
                                            </label>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                ({selectedEquipment.length} {selectedEquipment.length === 1 ? 'item' : 'items'})
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {selectedEquipment.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedEquipment([])}
                                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors duration-200"
                                                >
                                                    Clear All
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => setShowEquipmentModal(true)}
                                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-md transition-colors duration-200"
                                            >
                                                <Plus size={16} className="mr-1.5" />
                                                Add Equipment
                                            </button>
                                        </div>
                                    </div>
                                    <div className="h-[240px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent pr-2 -mr-2">
                                        {selectedEquipment.length > 0 ? (
                                            <div className="space-y-4">
                                                {/* Group equipment by type and subtype */}
                                                {Object.entries(
                                                    selectedEquipment.reduce((acc, eq) => {
                                                        // Create a key based on type and subtype
                                                        const typeKey = eq.equipment_type?.name || 'Unknown Type';
                                                        const subtypeKey = eq.equipment_subtype_id && eq.equipment_type?.subtypes?.find(
                                                            s => s.id === eq.equipment_subtype_id
                                                        )?.name;
                                                        
                                                        const key = subtypeKey ? `${typeKey} > ${subtypeKey}` : typeKey;
                                                        
                                                        if (!acc[key]) {
                                                            acc[key] = [];
                                                        }
                                                        acc[key].push(eq);
                                                        return acc;
                                                    }, {} as Record<string, typeof selectedEquipment>)
                                                ).map(([typeGroup, equipmentItems]) => (
                                                    <div key={typeGroup} className="border dark:border-gray-700 rounded-md overflow-hidden">
                                                        <div className="bg-gray-100 dark:bg-gray-700 px-3 py-2 font-medium text-sm text-gray-700 dark:text-gray-300">
                                                            {typeGroup}
                                                        </div>
                                                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                                            {equipmentItems.map((eq, index) => (
                                                                <div
                                                                    key={`${eq.id}-${index}`}
                                                                    className="flex justify-between items-start p-3 bg-white dark:bg-gray-800"
                                                                >
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="flex items-center gap-2 flex-wrap">
                                                                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                                                {eq.name}
                                                                            </p>
                                                                            <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                                                                                ID: {eq.id}
                                                                            </span>
                                                                            {/* {eq.groupedIds && eq.groupedIds.length > 0 && (
                                                                                <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded">
                                                                                    Group IDs: {eq.groupedIds.join(', ')}
                                                                                </span>
                                                                            )} */}
                                                                            {!eq.serial_number && (
                                                                                <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                                                                                    Qty: {eq.quantity || 1}
                                                                                </span>
                                                                            )}
                                                                            {eq.order_number && (
                                                                                <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded">
                                                                                    Order #{eq.order_number}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                                            {eq.serial_number && `S/N: ${eq.serial_number} • `}
                                                                            {eq.model_number && `Model: ${eq.model_number} • `}
                                                                            {eq.allocationType === 'store' ? 
                                                                                `Store: ${eq.store_number || 'Unknown'}` : 
                                                                                `Allocated to Equipment: #${eq.allocatedId || 'Unknown'}`}
                                                                        </p>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setSelectedEquipment(prev =>
                                                                                prev.filter((_, i) => i !== selectedEquipment.indexOf(eq))
                                                                            );
                                                                        }}
                                                                        className="ml-3 p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-center h-full bg-white dark:bg-gray-800 rounded-md">
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    No equipment added yet
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Pictures Section */}
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                                    Pictures
                                </label>
                                
                                {/* Picture List */}
                                {pictures.length > 0 && (
                                    <div className="mb-4">
                                        <div className="flex overflow-x-auto pb-4 space-x-4 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
                                            {pictures.map((pic, index) => (
                                                <div key={index} className="relative flex-none w-48">
                                                    <div className="relative group">
                                                        <img
                                                            src={URL.createObjectURL(pic)}
                                                            alt={`Preview ${index + 1}`}
                                                            className="w-full h-32 object-cover rounded-lg"
                                                        />
                                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity duration-200 rounded-lg" />
                                                        <button
                                                            type="button"
                                                            onClick={() => removePicture(index)}
                                                            className="absolute top-2 right-2 p-1 bg-red-100 text-red-600 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-200 transition-all duration-200"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                                                        {pic.name}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Picture Upload Buttons */}
                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={startCamera}
                                        className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                                    >
                                        <Camera size={16} className="mr-2" />
                                        Take Picture
                                    </button>
                                    <label className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 cursor-pointer">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            className="hidden"
                                            multiple
                                        />
                                        <Plus size={16} className="mr-2" />
                                        Upload Pictures
                                    </label>
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Footer - Fixed */}
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="create-task-form"
                                disabled={isLoading}
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-500 rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                {isLoading ? 'Creating...' : activeTab === 'sop' ? 'Start SOP' : 'Create Task'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showEquipmentModal && (
                <AddTaskEquipmentModal
                    isOpen={showEquipmentModal}
                    onClose={() => setShowEquipmentModal(false)}
                    onAdd={handleAddEquipment}
                    existingEquipmentIds={selectedEquipment.map(eq => eq.id)}
                    jobcardStoreNumber={jobcardStoreNumber}
                />
            )}
        </div>
    );
} 